import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Mail, AlertTriangle, CheckCircle2, ChevronDown, Send, Settings2, ExternalLink } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { apiGet, apiPost } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STEPS = [
  "Open your Gmail account",
  "Enable 2-Factor Authentication on the account",
  "Go to Google Account → Security → App Passwords",
  "Create an App Password for ‘Mail’ on ‘Other (custom name)’",
  "Copy the 16-character password (no spaces)",
  "Paste both values into the SMTP settings page below — OR set them in /app/backend/.env as MAIL_USERNAME and MAIL_PASSWORD",
  "Click ‘Send test email’ to confirm delivery",
];

/** Big-and-clear email status panel + 7-step setup guide + test-send button. */
const EmailConfigCard = () => {
  const [smtp, setSmtp] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [lastTestAt, setLastTestAt] = useState(null);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        apiGet("/admin/smtp-status"),
        apiGet("/admin/email-stats"),
      ]);
      setSmtp(s);
      setStats(t);
      // Last test-email log (best effort)
      try {
        const logs = await apiGet("/admin/email-logs?email_type=test&limit=1");
        const latest = (logs?.items || logs || [])[0];
        if (latest?.created_at) setLastTestAt(latest.created_at);
      } catch { /* ignore */ }
    } catch (e) {
      // admin only
    }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    try {
      setTesting(true);
      const target = testTo.trim() || undefined;
      const res = await apiPost("/admin/smtp/test", target ? { to: target } : {});
      if (res?.ok) {
        toast.success(`Test email queued to ${res.to}`);
        setLastTestAt(new Date().toISOString());
      } else {
        toast.error(res?.error || "Test send failed — check Email Logs");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (!smtp) return null;
  const isDev = !!smtp.dev_mode;
  // The backend's GET /admin/smtp-status returns the boolean as
  // `configured` (see email_service.py:421 / server.py SMTP-status route).
  // A stray `smtp_configured` read here — which NO other consumer uses —
  // always evaluated to undefined → falsy → the "Email not configured"
  // banner would show even when SMTP was working and test emails were
  // delivering. Accept both spellings defensively so any future
  // renaming doesn't regress this card.
  const isConfigured = !!(smtp.configured ?? smtp.smtp_configured);

  return (
    <Card data-testid="email-config-card" className="border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <Mail className="h-4 w-4 text-primary" /> Email configuration
        </CardTitle>
        <CardDescription>
          Verification, approval, and confirmation emails. Required before sending the soft-launch link to real users.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Big status pill */}
        {isConfigured && !isDev ? (
          <div
            data-testid="email-config-status-ok"
            className="rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-4 py-3 flex items-start gap-3"
          >
            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">Email configured</div>
              <div className="text-xs opacity-80">
                Sending from {smtp?.config?.username || smtp?.from || "(configured)"}.
                {lastTestAt && (
                  <> · Last test email: {new Date(lastTestAt).toLocaleString()}</>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            data-testid="email-config-status-missing"
            className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">Email not configured</div>
              <div className="text-xs opacity-80">
                Verification and notification emails will not send. Follow the setup instructions below before launch.
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="email-config-stats">
            <Stat label="Sent today" value={stats.sent || 0} />
            <Stat label="Failed today" value={stats.failed || 0} tone={stats.failed > 0 ? "warn" : null} />
            <Stat label="Skipped (dev)" value={stats.skipped || 0} />
            <Stat label="Total today" value={stats.total || 0} />
          </div>
        )}

        {/* Test send */}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <label className="flex-1">
            <span className="text-xs text-muted-foreground">Send test email to</span>
            <Input
              data-testid="email-config-test-to"
              placeholder="(defaults to admin notification email)"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
          </label>
          <Button
            onClick={sendTest}
            disabled={testing}
            data-testid="email-config-send-test"
          >
            <Send className="h-4 w-4" /> {testing ? "Sending…" : "Send test email"}
          </Button>
          <Button asChild variant="outline" data-testid="email-config-open-settings">
            <Link to="/settings#email-configuration">
              <Settings2 className="h-4 w-4" /> SMTP settings
            </Link>
          </Button>
        </div>

        {/* Setup instructions */}
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2"
              data-testid="email-config-toggle-guide"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
              />
              7-step Gmail setup guide
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <ol className="space-y-1.5 pl-1 text-sm" data-testid="email-config-setup-steps">
              {STEPS.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <Badge variant="outline" className="h-5 min-w-5 px-1.5 text-[10px]">
                    {i + 1}
                  </Badge>
                  <span className="flex-1 text-muted-foreground">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1">
              <div className="font-semibold text-foreground">backend/.env keys</div>
              <code className="block">MAIL_USERNAME=your@gmail.com</code>
              <code className="block">MAIL_PASSWORD=your-16-char-app-password</code>
              <code className="block">MAIL_FROM=your@gmail.com</code>
              <div className="text-muted-foreground pt-2">
                Then run: <code>supervisorctl restart backend</code>
              </div>
            </div>
            <div className="mt-3">
              <Button asChild variant="link" size="sm" className="px-0" data-testid="email-config-google-link">
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3 w-3" /> Open Google App Passwords
                </a>
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-md border border-border bg-muted/20 px-3 py-2 ${tone === "warn" ? "border-[hsl(var(--warning))]/40" : ""}`}>
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="text-base font-semibold">{value}</div>
  </div>
);

export default EmailConfigCard;
